import { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'en' | 'sk';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    'nav.howItWorks': 'How It Works',
    'nav.benefits': 'Benefits',
    'nav.showcase': 'Showcase',
    'nav.pricing': 'Pricing',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact',
    
    // Marquee
    'marquee.text': "We don't ask what website you want. We find out what website you need – and build it in 3 days.",
    'hero.aiWebsiteCreation': 'AI-Powered Website Creation',
    'hero.startWebsite': 'Start Your Website',
    'hero.title': 'AI‑Built Websites',
    'hero.titleHighlight': 'Fast. Beautiful. No Code.',
    'hero.subtitle': 'Your website ready in 3 days • From €799',
    
    // What You Get Section
    'whatYouGet.title': 'What You Get',
    'whatYouGet.subtitle': 'Clear benefits without hidden fees',
    'whatYouGet.days': 'days',
    'whatYouGet.from': 'from',
    'whatYouGet.revisionsUnit': 'revisions',
    'whatYouGet.speed.label': 'Fast Delivery',
    'whatYouGet.speed.desc': 'Online in days, not months',
    'whatYouGet.price.label': 'Fixed Price',
    'whatYouGet.price.desc': 'No surprises, know the price upfront',
    'whatYouGet.revisions.label': 'Revisions Included',
    'whatYouGet.revisions.desc': 'Adjustments included, no extra fees',
    'whatYouGet.guarantee.label': 'Satisfaction Guarantee',
    'whatYouGet.guarantee.desc': 'Satisfied or money back',
    
    // How It Works
    'howItWorks.title': 'How It Works',
    'howItWorks.subtitle': 'From concept to completion in four simple steps',
    'howItWorks.step1.day': 'Day 0',
    'howItWorks.step1.title': 'Describe your idea',
    'howItWorks.step1.desc': 'Explain what you need in plain language. No technical knowledge required.',
    'howItWorks.step2.day': 'Day 1',
    'howItWorks.step2.title': 'AI specialist creates your website',
    'howItWorks.step2.desc': 'Our AI specialist creates the structure, layout, content, and design within minutes.',
    'howItWorks.step3.day': 'Day 2-3',
    'howItWorks.step3.title': 'We refine it together',
    'howItWorks.step3.desc': 'We adjust the site to fit your needs: images, forms, copy, colors.',
    'howItWorks.step4.day': 'Day 3',
    'howItWorks.step4.title': 'Ready to launch',
    'howItWorks.step4.desc': 'Your site is delivered in clean code or deployed directly to your CMS.',
    
    // Benefits
    'benefits.title': 'No More Months of Waiting and Thousands for Agencies',
    'benefits.subtitle': 'Get a professional website in days, not months. At a fraction of traditional agency prices.',
    'benefits.fast.title': 'Stop Waiting for Weeks',
    'benefits.fast.desc': 'While agencies deliver in 4-8 weeks, you get your website in 3 days. Start earning sooner.',
    'benefits.affordable.title': 'Save Thousands',
    'benefits.affordable.desc': 'Website for €799 instead of €3000-5000 at traditional agencies. Same quality, 4x lower price.',
    'benefits.responsive.title': 'More Customers from Mobile',
    'benefits.responsive.desc': '70% of people browse on mobile. Your site will look perfect on every device = more sales.',
    'benefits.seo.title': 'Get Found on Google',
    'benefits.seo.desc': 'Website optimized so customers find you when searching for your services. No extra fees.',
    'benefits.updates.title': 'No More Complicated Revisions',
    'benefits.updates.desc': 'Forget endless emails and misunderstandings. Tell us what you want, we make it happen. Simple.',
    
    // Guarantee
    'guarantee.badge': 'Iron-Clad Guarantee',
    'guarantee.title': 'Afraid of Wasting Your Money?',
    'guarantee.subtitle': "If anything isn't to your liking, you get your money back. No questions, no excuses.",
    'guarantee.satisfaction.title': '14 Days to Decide',
    'guarantee.satisfaction.desc': 'After delivery, you have 14 days to test. Not working for you? Full refund.',
    'guarantee.revisions.title': '3 Revision Rounds Included',
    'guarantee.revisions.desc': `Not "multiple". Exactly 3 rounds of changes until it's exactly what you want.`,
    'guarantee.moneyBack.title': '100% Back Means 100% Back',
    'guarantee.moneyBack.desc': `No "processing fees". No "admin costs". The full amount, within 5 days.`,
    'guarantee.delivery.title': 'Late? You Pay Less',
    'guarantee.delivery.desc': "5% discount for every day we're late. 3 days late = 15% back.",
    'guarantee.cta.title': 'Still Have Questions?',
    'guarantee.cta.description': 'Call or message us. We respond within 24 hours.',
    'guarantee.cta.noRisk': 'No Obligation',
    'guarantee.cta.guaranteed': 'Free Consultation',
    
    // Testimonials
    'testimonials.badge': 'Client Reviews',
    'testimonials.title': 'What Our Clients Say',
    'testimonials.subtitle': 'Real feedback from businesses we\'ve helped transform their online presence',
    'testimonials.client1.name': 'Martin Novák',
    'testimonials.client1.role': 'CEO, TechStart',
    'testimonials.client1.text': 'Amazing speed and quality. Our website was ready in 2 days and looks incredibly professional. The AI approach saved us weeks of development time.',
    'testimonials.client2.name': 'Jana Kováčová',
    'testimonials.client2.role': 'Marketing Manager, GrowthCo',
    'testimonials.client2.text': 'I was skeptical about AI-generated websites, but the result exceeded all expectations. Modern design, perfect functionality, and unbeatable price.',
    'testimonials.client3.name': 'Peter Horváth',
    'testimonials.client3.role': 'Founder, FitLife Studio',
    'testimonials.client3.text': 'The only issue was that we didn\'t have a completely clear vision on our side. But together we were able to translate it into results. Fast communication, multiple revisions included, and the final result is stunning. Highly recommended!',
    'testimonials.client4.name': 'Lucia Tóthová',
    'testimonials.client4.role': 'Owner, Beauty Salon',
    'testimonials.client4.text': 'Our new website brings us more clients than ever before. Professional look, easy to navigate, and it works perfectly on mobile devices.',
    'testimonials.client5.name': 'Marek Szabó',
    'testimonials.client5.role': 'Director, Legal Services',
    'testimonials.client5.text': 'Excellent experience from start to finish. The website reflects our professional image and we received it even faster than promised.',
    'testimonials.client6.name': 'Rastislav Štulrajter',
    'testimonials.client6.role': 'CEO, FVEnergy',
    'testimonials.client6.text': 'The quality-to-price ratio is unmatched. We got a modern, fast website that increased our conversions by 40% in the first month.',
    'testimonials.stats.projects': 'Completed Projects',
    'testimonials.stats.satisfaction': 'Satisfaction Rate',
    'testimonials.stats.rating': 'Average Rating',
    'testimonials.stats.response': 'Response Time',
    
    // Pricing
    'pricing.title': 'Pricing',
    'pricing.subtitle': 'Choose the perfect plan for your project',
    'pricing.description': 'All plans include AI-powered design, development, optimization, and launch support.',
    'pricing.additionalNote': 'Once you exceed the number of revisions, you can purchase additional hours during which we will make adjustments. Each hour started is €60.',
    'pricing.customNote': 'We will customize the content and structure of the website to your needs',
    'pricing.getStarted': 'Get Started',
    'pricing.oneTime': 'One-time',
    'pricing.starter.name': 'Landing Pro',
    'pricing.starter.useCase': 'One powerful page that converts visitors into customers',
    'pricing.business.name': 'Web Complete',
    'pricing.business.useCase': 'Everything your business needs in one complete package',
    'pricing.premium.name': 'Brand Builder',
    'pricing.premium.useCase': 'Build a memorable brand with full digital identity',
    
    // Pricing Features
    'pricing.features.completeLanding': '1-3 page website with hero, benefits, testimonials & contact sections',
    'pricing.features.multiPageWeb': 'Multi-page website with complete structure',
    'pricing.features.extensiveWeb': 'Extensive website with full portfolio and presentation',
    'pricing.features.advancedSalesStructure': 'Advanced sales structure',
    'pricing.features.contactIntegration': 'Contact form integration',
    'pricing.features.mobileResponsive': 'Mobile responsive design',
    'pricing.features.basicSEO': 'Basic SEO optimization',
    'pricing.features.advancedSEO': 'Advanced SEO optimization',
    'pricing.features.2revisions': '2 revisions included in price',
    'pricing.features.3dayDelivery': 'Express delivery within 3 days',
    'pricing.features.5dayDelivery': 'Delivery within 5 business days',
    'pricing.features.advancedContactForms': 'Advanced contact forms',
    'pricing.features.imageGallery': 'Image gallery/portfolio',
    'pricing.features.aiBanners': 'Creation of AI banners/visuals',
    'pricing.features.faq': 'FAQ',
    'pricing.features.3revisions': '3 revisions included in price',
    'pricing.features.5revisions': '5 revisions included in price',
    'pricing.features.logoAI': 'Logo with AI',
    'pricing.features.specialColors': 'Brand colors customization',
    'pricing.features.socialIntegration': 'Social media integration',
    'pricing.features.7dayDelivery': 'Complete delivery within 7 days',
    'pricing.features.3to5Subpages': '3 - 5 subpages',
    'pricing.features.5to8Subpages': '5+ subpages',
    // Tiered animations
    'pricing.features.basicAnimations': 'Basic scroll animations',
    'pricing.features.advancedAnimations': 'Advanced animations & transitions',
    'pricing.features.premiumAnimations': 'Premium animations + micro-interactions',
    // Exclusive features
    'pricing.features.analytics': 'Google Analytics integration',
    'pricing.features.blogSection': 'Blog section',
    'pricing.features.brandingGuide': 'Branding guide & style manual',
    'pricing.features.videoSection': 'Video section / Video background',
    'pricing.features.prioritySupport': 'Priority processing',
    'pricing.features.apiIntegration': 'API integrations (Calendly, calendar, etc.)',
    
    'pricing.allInclude': 'All packages include',

    // Pricing Comparison Table
    'pricing.comparison.title': 'Package Comparison',
    'pricing.comparison.feature': 'Feature',
    'pricing.comparison.pages': 'Number of pages',
    'pricing.comparison.aiBanners': 'AI banners & visuals',
    'pricing.comparison.contactForm': 'Contact form',
    'pricing.comparison.gallery': 'Gallery / Portfolio',
    'pricing.comparison.faq': 'FAQ section',
    'pricing.comparison.social': 'Social media integration',
    'pricing.comparison.aiLogo': 'AI generated logo',
    'pricing.comparison.colorScheme': 'Brand colors customization',
    'pricing.comparison.animations': 'Animations',
    'pricing.comparison.responsive': 'Mobile responsive',
    'pricing.comparison.seo': 'SEO optimization',
    'pricing.comparison.revisions': 'Number of revisions',
    'pricing.comparison.delivery': 'Delivery time',
    'pricing.comparison.basic': 'Basic',
    'pricing.comparison.advanced': 'Advanced',
    'pricing.comparison.days': 'days',
    'pricing.comparison.analytics': 'Analytics integration',
    'pricing.comparison.blog': 'Blog section',
    'pricing.comparison.brandingGuide': 'Branding guide',
    'pricing.comparison.videoSection': 'Video section',
    'pricing.comparison.prioritySupport': 'Priority processing',
    'pricing.comparison.apiIntegration': 'API integrations',
    'pricing.comparison.animationsBasic': 'Basic',
    'pricing.comparison.animationsAdvanced': 'Advanced',
    'pricing.comparison.animationsPremium': 'Premium',
    'pricing.comparison.seoBasic': 'Basic',
    'pricing.comparison.seoAdvanced': 'Advanced',
    
    // Showcase
    'showcase.title': 'This Is How Your Website Could Look',
    'showcase.subtitle': 'Get inspired by modern designs. Each website is custom-built to fit your needs.',
    'showcase.description': 'Examples of styles and functionality we can create for you - from simple landing pages to complex solutions.',
    'showcase.visitWebsite': 'Visit Website',
    'showcase.example1.title': 'E-commerce Store',
    'showcase.example1.desc': 'Modern online store with product catalog and shopping cart',
    'showcase.example2.title': 'Restaurant Website',
    'showcase.example2.desc': 'Elegant restaurant presentation with menu and reservations',
    'showcase.example3.title': 'Tech Startup',
    'showcase.example3.desc': 'Innovative startup landing page with modern design',
    'showcase.ctaTitle': 'Like What You See?',
    'showcase.ctaDescription': 'In 3 days, you could have your own website in a similar style.',
    'showcase.ctaButton': 'I Want My Website',
    
    // Website Types
    'websiteTypes.title': 'Who We Build For',
    'websiteTypes.subtitle': 'Find yourself among our clients and discover how we can help you',
    'websiteTypes.ecommerce.title': 'E-commerce & Products',
    'websiteTypes.ecommerce.desc': 'Selling products online? You need a website that showcases your goods professionally and converts visitors into customers.',
    'websiteTypes.ecommerce.identify': 'I have a product and want to sell it',
    'websiteTypes.services.title': 'Services & Freelancers',
    'websiteTypes.services.desc': 'Are you a hairdresser, photographer, accountant, or other service provider? We help you get more clients and build trust online.',
    'websiteTypes.services.identify': 'I offer services and need clients',
    'websiteTypes.startups.title': 'Startups & Projects',
    'websiteTypes.startups.desc': 'Launching a new idea? Need to quickly test the market or attract investors? A landing page that impresses in just a few days.',
    'websiteTypes.startups.identify': "I'm starting something new",
    'websiteTypes.established.title': 'Companies & Brands',
    'websiteTypes.established.desc': 'Have a running business but your website is outdated? We help you with a modern presentation that reflects your quality.',
    'websiteTypes.established.identify': 'I need a change / upgrade',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Everything you need to know about our AI website creation process',
    'faq.q1': 'How fast will my website be ready?',
    'faq.a1': 'Depending on the package, between 1 and 3 days. Our AI-powered process significantly reduces development time while maintaining high quality.',
    'faq.q2': 'Can I update the website later?',
    'faq.a2': 'Yes. We can regenerate new versions quickly based on new prompts or edits. Our AI system makes updates much faster than traditional development.',
    'faq.q3': 'What is the final output?',
    'faq.a3': 'You can download the complete code from this interface and use it anywhere you want. Alternatively, we can host it for you, which makes it easy to make additional changes anytime.',
    'faq.q4': 'Is the site SEO-optimized?',
    'faq.a4': 'Yes. Your site includes clean headers, metadata, fast performance, and image optimization. We follow current SEO best practices to help your site rank well.',
    'faq.q5': 'Do you provide hosting?',
    'faq.a5': 'Yes, we provide reliable hosting with easy access for future updates. Or you can download the code and host it anywhere you prefer - you have full flexibility.',
    'faq.q6': 'What if I\'m not satisfied with the result?',
    'faq.a6': 'We work closely with you during the refinement process to ensure the final result meets your expectations. Our iterative approach means we can adjust and improve until you\'re happy.',
    'faq.q7': 'What\'s the difference between packages and which one should I choose?',
    'faq.a7': 'Landing Pro (€799) is ideal for single landing pages - product launches, services, or campaigns. Web Complete (€1,199) is our most popular choice for companies needing a multi-page website with analytics integration and blog section. Brand Builder (€1,899) is for established brands requiring extensive web presence with branding guide, video section, and priority processing. If unsure, Web Complete is the best value for most businesses.',
    'faq.q8': 'What are the payment options and do you issue invoices?',
    'faq.a8': 'We accept bank transfers and card payments. Payment is made after approving the design - 50% upfront, 50% upon completion. We issue a proper invoice for every order, which you can use for tax deductions as a business expense.',
    'faq.q9': 'What happens after the project is completed? What support options are available?',
    'faq.a9': 'After completion, you receive the full website with all files. We offer hosting with easy access for future updates. If you need changes later, you can order additional hours at €60/hour. We also provide ongoing maintenance packages for clients who need regular updates and support.',
    'faq.q10': 'How does the 14-day guarantee work?',
    'faq.a10': 'After we deliver your website, you have 14 days to test it thoroughly. If for any reason the website doesn\'t meet your expectations and we can\'t find a solution together, we\'ll refund your full payment. No complicated forms, no excuses - just a simple refund within 5 business days.',
    'faq.q11': 'What if I need more than 3 revision rounds?',
    'faq.a11': 'Each package includes exactly 3 rounds of revisions - which is usually more than enough. If you need additional changes after using all 3 rounds, you can order extra revisions at €60/hour. We always try to be as flexible as possible to ensure your complete satisfaction.',
    'faq.q12': 'What happens if you\'re late with delivery?',
    'faq.a12': 'We stand behind our deadlines. If we\'re late, you get a 5% discount for each day of delay. For example: 3 days late = 15% back from your payment. It\'s our commitment that you either get your website on time, or you pay less. Simple and fair.',
    
    // Cookies
    'cookies.title': 'We use cookies',
    'cookies.description': 'We use cookies to improve your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept", you consent to our use of cookies.',
    'cookies.accept': 'Accept All',
    'cookies.decline': 'Decline',
    'cookies.preferences': 'Preferences',
    
    // Cookie Preferences
    'cookiePreferences.title': 'Cookie Preferences',
    'cookiePreferences.description': 'We use cookies to enhance your browsing experience and analyze our traffic. You can customize your cookie preferences below.',
    'cookiePreferences.necessary.title': 'Necessary Cookies',
    'cookiePreferences.necessary.description': 'These cookies are essential for the website to function properly. They cannot be disabled.',
    'cookiePreferences.analytics.title': 'Analytics Cookies',
    'cookiePreferences.analytics.description': 'These cookies help us understand how visitors interact with our website by collecting anonymous information.',
    'cookiePreferences.marketing.title': 'Marketing Cookies',
    'cookiePreferences.marketing.description': 'These cookies are used to track visitors across websites to display relevant advertisements.',
    'cookiePreferences.savePreferences': 'Save Preferences',
    'cookiePreferences.acceptAll': 'Accept All',
    
    // Contact
    'contact.title': 'Book Your Free 15-Minute Consultation',
    'contact.subtitle': "We'll discuss your project, answer your questions, and suggest the best solution. No commitment - just 15 minutes of your time.",
    'contact.name': 'Name',
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.currentWebsite': 'Current Website',
    'contact.websiteType': 'What kind of website do you need?',
    'contact.description': 'Describe your idea or goal',
    'contact.namePlaceholder': 'Your full name',
    'contact.emailPlaceholder': 'your.email@example.com',
    'contact.phonePlaceholder': '+421 123 456 789',
    'contact.currentWebsitePlaceholder': 'https://www.yourwebsite.com',
    'contact.websiteTypePlaceholder': 'Select website type',
    'contact.descriptionPlaceholder': 'Tell us about your business, target audience, goals, and any specific requirements...',
    'contact.submitRequest': 'Get My Free Consultation',
    'contact.submitting': 'Submitting...',
    'contact.freeConsultation': 'Free 15-minute consultation',
    'contact.responseTime': "We'll contact you within 24h",
    'contact.noCommitment': 'No commitment',
    'contact.bookConsultation': 'Book a free consultation',
    'contact.requestSubmitted': 'Request Submitted!',
    'contact.responseMessage': "Thank you! We'll contact you within 24 hours to schedule your 15-minute consultation.",
    'contact.socialProof.since': 'Building websites since 2025',
    'contact.socialProof.clients': 'Trusted by businesses across Slovakia',
    'contact.ecommerceWebsite': 'E-commerce / Product Website',
    'contact.servicesWebsite': 'Services / Company Website',
    'contact.startupWebsite': 'Startup / Landing Page',
    'contact.rebrandWebsite': 'Website Modernization',
    'contact.other': 'Other',
    
    // Campaign Questions
    'contact.campaign.basicInfo': 'Basic Campaign Info',
    'contact.campaign.goal': 'What is the main goal of the campaign?',
    'contact.campaign.goalPlaceholder': 'sales, leads, registrations, other',
    'contact.campaign.product': 'What product/service are we promoting?',
    'contact.campaign.productPlaceholder': 'Describe your product or service',
    'contact.campaign.targetGroup': 'What is the main target group?',
    'contact.campaign.targetGroupPlaceholder': 'age, demographics, interests',
    
    'contact.campaign.contentOffer': 'Content & Offer',
    'contact.campaign.mainOffer': 'What is the main offer or benefit for visitors?',
    'contact.campaign.mainOfferPlaceholder': 'discount, free trial, consultation',
    'contact.campaign.contentReady': 'Do you have prepared text/product description, or should we create it?',
    'contact.campaign.contentReadyPlaceholder': 'ready / need creation',
    'contact.campaign.media': 'Do you have photos, video or graphics we must use?',
    'contact.campaign.mediaPlaceholder': 'links to materials or "no"',
    
    'contact.campaign.structure': 'Structure & Elements',
    'contact.campaign.formButton': 'Do you need a form, buy button or other action on the page?',
    'contact.campaign.formButtonPlaceholder': 'contact form, buy button, registration',
    'contact.campaign.sections': 'What sections are essential?',
    'contact.campaign.sectionsPlaceholder': 'intro, benefits, reviews, FAQ, contact',
    'contact.campaign.countdown': 'Should the page include countdown timer or limited offer?',
    'contact.campaign.countdownPlaceholder': 'yes / no',
    
    'contact.campaign.brand': 'Brand & Style',
    'contact.campaign.colors': 'What are your brand colors and fonts?',
    'contact.campaign.colorsPlaceholder': 'blue, white, Arial / or "create new"',
    'contact.campaign.tone': 'Should the page feel formal, friendly or premium?',
    'contact.campaign.tonePlaceholder': 'formal / friendly / premium',
    'contact.campaign.examples': 'Do you have examples of pages you like?',
    'contact.campaign.examplesPlaceholder': 'links or descriptions',
    
    'contact.campaign.technical': 'Technical & Practical',
    'contact.campaign.url': 'What is the planned URL or domain?',
    'contact.campaign.urlPlaceholder': 'yoursite.com or subdomain',
    'contact.campaign.standalone': 'Should the page run standalone or be part of your website?',
    'contact.campaign.standalonePlaceholder': 'standalone / part of existing site',
    
    // Product Questions
    'contact.product.basicInfo': 'Basic Product Info',
    'contact.product.type': 'What type of product are you selling?',
    'contact.product.typePlaceholder': 'physical product, software, course, service',
    'contact.product.uniqueness': 'What makes your product unique?',
    'contact.product.uniquenessPlaceholder': 'key differentiators and benefits',
    'contact.product.targetCustomer': 'Who is your ideal customer?',
    'contact.product.targetCustomerPlaceholder': 'demographics, needs, pain points',
    
    'contact.product.productDetails': 'Product Details',
    'contact.product.features': 'What are the key features?',
    'contact.product.featuresPlaceholder': 'list main features and benefits',
    'contact.product.pricing': 'What is your pricing model?',
    'contact.product.pricingPlaceholder': 'one-time, subscription, tiered pricing',
    'contact.product.competition': 'Who are your main competitors?',
    'contact.product.competitionPlaceholder': 'competitive landscape',
    
    'contact.product.presentation': 'Product Presentation',
    'contact.product.showcase': 'How should we showcase the product?',
    'contact.product.showcasePlaceholder': 'demos, screenshots, videos, testimonials',
    'contact.product.callToAction': 'What is the main call-to-action?',
    'contact.product.callToActionPlaceholder': 'buy now, try free, request demo',
    'contact.product.trust': 'What trust elements do you have?',
    'contact.product.trustPlaceholder': 'testimonials, reviews, guarantees, certifications',
    
    // Service Questions
    'contact.service.basicInfo': 'Basic Service Info',
    'contact.service.type': 'What type of service do you provide?',
    'contact.service.typePlaceholder': 'consulting, repair, design, coaching',
    'contact.service.process': 'What is your service process?',
    'contact.service.processPlaceholder': 'step-by-step how you work with clients',
    'contact.service.results': 'What results do clients achieve?',
    'contact.service.resultsPlaceholder': 'success stories, outcomes, benefits',
    
    'contact.service.positioning': 'Service Positioning',
    'contact.service.experience': 'What is your experience/expertise?',
    'contact.service.experiencePlaceholder': 'years, certifications, notable projects',
    'contact.service.areas': 'What areas/industries do you serve?',
    'contact.service.areasPlaceholder': 'geographic areas, industries, niches',
    'contact.service.approach': 'What is your unique approach?',
    'contact.service.approachPlaceholder': 'methodology, philosophy, differentiation',
    
    'contact.service.booking': 'Booking & Contact',
    'contact.service.consultation': 'Do you offer free consultation?',
    'contact.service.consultationPlaceholder': 'yes / no, duration, what\'s included',
    'contact.service.pricing': 'How is your service priced?',
    'contact.service.pricingPlaceholder': 'hourly, project-based, packages',
    'contact.service.bookingMethod': 'How should clients book/contact you?',
    'contact.service.bookingMethodPlaceholder': 'online booking, phone, email, contact form',
    
    // Landing Page Questions
    'contact.landing.basicInfo': 'Basic Landing Page Info',
    'contact.landing.source': 'Where will traffic come from?',
    'contact.landing.sourcePlaceholder': 'Google Ads, Facebook, email, organic',
    'contact.landing.goal': 'What is the single main goal?',
    'contact.landing.goalPlaceholder': 'email signup, purchase, download, registration',
    'contact.landing.offer': 'What is your irresistible offer?',
    'contact.landing.offerPlaceholder': 'discount, free trial, bonus, guarantee',
    
    'contact.landing.optimization': 'Optimization & Conversion',
    'contact.landing.headline': 'What should be the main headline?',
    'contact.landing.headlinePlaceholder': 'compelling headline that grabs attention',
    'contact.landing.objections': 'What objections might visitors have?',
    'contact.landing.objectionsPlaceholder': 'price, trust, time, need concerns',
    'contact.landing.urgency': 'What creates urgency?',
    'contact.landing.urgencyPlaceholder': 'limited time, limited quantity, bonus expires',
    
    'contact.landing.testing': 'Testing & Analytics',
    'contact.landing.variants': 'Do you want to test multiple versions?',
    'contact.landing.variantsPlaceholder': 'A/B test headlines, offers, layouts',
    'contact.landing.tracking': 'What tracking do you need?',
    'contact.landing.trackingPlaceholder': 'Google Analytics, Facebook Pixel, conversion tracking',
    'contact.landing.followUp': 'What happens after conversion?',
    'contact.landing.followUpPlaceholder': 'thank you page, email sequence, redirect',
    
    // Rebrand Questions
    'contact.rebrand.currentState': 'Current Website State',
    'contact.rebrand.problems': 'What problems does your current website have?',
    'contact.rebrand.problemsPlaceholder': 'outdated design, slow loading, not mobile-friendly, poor UX',
    'contact.rebrand.keepFunctionality': 'What functionality do you want to keep?',
    'contact.rebrand.keepFunctionalityPlaceholder': 'contact form, blog, gallery, specific integrations',
    'contact.rebrand.newRequirements': 'New Requirements',
    'contact.rebrand.newFeatures': 'What new features or sections do you need?',
    'contact.rebrand.newFeaturesPlaceholder': 'online booking, e-shop, testimonials, portfolio',
    'contact.rebrand.goal': 'What is the main goal of the modernization?',
    'contact.rebrand.goalPlaceholder': 'more clients, better conversions, modern look, improved UX',
    'contact.rebrand.competitors': 'Which competitors\' websites do you like?',
    'contact.rebrand.competitorsPlaceholder': 'competitor1.com, competitor2.com',
    'contact.rebrand.brandContent': 'Brand & Content',
    'contact.rebrand.brandChanges': 'Will the brand (logo, colors) change too?',
    'contact.rebrand.brandChangesPlaceholder': 'yes / no / partially',
    'contact.rebrand.contentUpdate': 'Do you have updated texts and photos?',
    'contact.rebrand.contentUpdatePlaceholder': 'yes / need to create / partially ready',
    'contact.rebrand.timeline': 'When do you need the new website?',
    'contact.rebrand.timelinePlaceholder': 'as soon as possible / within a month / no rush',
    
    // Footer
    'footer.description': 'AI-powered website creation. From idea to functional website in hours.',
    'footer.quickLinks': 'Quick Links',
    'footer.contact': 'Contact'
  },
  sk: {
    // Navigation
    'nav.howItWorks': 'Ako to funguje',
    'nav.benefits': 'Výhody',
    'nav.showcase': 'Ukážky',
    'nav.pricing': 'Cenník',
    'nav.faq': 'FAQ',
    'nav.contact': 'Kontakt',
    
    // Marquee
    'marquee.text': 'Nepýtame sa, aký web chcete. Zistíme, aký web potrebujete – a do 3 dní ho postavíme.',
    
    // Hero Section
    'hero.aiWebsiteCreation': 'Tvorba webstránok s AI',
    'hero.startWebsite': 'Začať s webstránkou',
    'hero.title': 'Webstránky vytvorené AI',
    'hero.titleHighlight': 'Rýchlo. Krásne. Bez kódu.',
    'hero.subtitle': 'Vaša webstránka do 3 dní • Od 799 €',
    
    // What You Get Section
    'whatYouGet.title': 'Čo získate',
    'whatYouGet.subtitle': 'Jasné výhody bez skrytých poplatkov',
    'whatYouGet.days': 'dní',
    'whatYouGet.from': 'od',
    'whatYouGet.revisionsUnit': 'revízií',
    'whatYouGet.speed.label': 'Rýchle dodanie',
    'whatYouGet.speed.desc': 'Online za pár dní, nie mesiacov',
    'whatYouGet.price.label': 'Fixná cena',
    'whatYouGet.price.desc': 'Bez prekvapení, viete cenu vopred',
    'whatYouGet.revisions.label': 'Revízie v cene',
    'whatYouGet.revisions.desc': 'Úpravy zahrnuté, žiadne príplatky',
    'whatYouGet.guarantee.label': 'Garancia spokojnosti',
    'whatYouGet.guarantee.desc': 'Spokojní alebo peniaze späť',
    
    // How It Works
    'howItWorks.title': 'Ako to funguje',
    'howItWorks.subtitle': 'Od koncepcie po dokončenie v štyroch jednoduchých krokoch',
    'howItWorks.step1.day': 'Deň 0',
    'howItWorks.step1.title': 'Opíšte svoj nápad',
    'howItWorks.step1.desc': 'Vysvetlite čo potrebujete jednoduchým jazykom. Nepotrebujete technické znalosti.',
    'howItWorks.step2.day': 'Deň 1',
    'howItWorks.step2.title': 'AI špecialista vytvorí vašu webstránku',
    'howItWorks.step2.desc': 'Náš AI špecialista vytvorí štruktúru, rozloženie, obsah a dizajn za pár minút.',
    'howItWorks.step3.day': 'Deň 2-3',
    'howItWorks.step3.title': 'Spoločne to doladíme',
    'howItWorks.step3.desc': 'Prispôsobíme stránku vašim potrebám: obrázky, formuláre, texty, farby.',
    'howItWorks.step4.day': 'Deň 3',
    'howItWorks.step4.title': 'Pripravené na spustenie',
    'howItWorks.step4.desc': 'Vaša stránka je dodaná v čistom kóde alebo nasadená priamo do vášho CMS.',
    
    // Benefits
    'benefits.title': 'Už žiadne mesiace čakania a tisíce za agentúru',
    'benefits.subtitle': 'Získajte profesionálnu webstránku za dni, nie mesiace. Za zlomok ceny tradičných agentúr.',
    'benefits.fast.title': 'Koniec nekonečného čakania',
    'benefits.fast.desc': 'Kým agentúry dodávajú za 4-8 týždňov, vy máte hotovú stránku za 3 dni. Začnite zarábať skôr.',
    'benefits.affordable.title': 'Ušetrite tisíce eur',
    'benefits.affordable.desc': 'Webstránka za €799 namiesto €3000-5000 u tradičnej agentúry. Rovnaká kvalita, 4x nižšia cena.',
    'benefits.responsive.title': 'Viac zákazníkov z mobilov',
    'benefits.responsive.desc': '70% ľudí prehliada na mobile. Vaša stránka bude vyzerať perfektne na každom zariadení = viac predajov.',
    'benefits.seo.title': 'Nájdu vás na Google',
    'benefits.seo.desc': 'Stránka optimalizovaná tak, aby vás zákazníci našli, keď hľadajú vaše služby. Bez extra poplatkov.',
    'benefits.updates.title': 'Koniec komplikovaných revízií',
    'benefits.updates.desc': 'Zabudnite na nekonečné emaily a nedorozumenia. Poviete čo chcete, my to urobíme. Jednoducho.',
    
    // Guarantee
    'guarantee.badge': 'Železná záruka',
    'guarantee.title': 'Bojíte sa, že vyhodíte peniaze?',
    'guarantee.subtitle': 'Ak čokoľvek nebude podľa vašich predstáv, dostanete peniaze späť. Bez otázok, bez výhovoriek.',
    'guarantee.satisfaction.title': '14 dní na rozmyslenie',
    'guarantee.satisfaction.desc': 'Po dodaní máte 14 dní na otestovanie. Nefunguje vám? Vrátime všetko.',
    'guarantee.revisions.title': '3 kolá revízií v cene',
    'guarantee.revisions.desc': `Nie „viaceré". Presne 3 kolá úprav, kým to nebude presne podľa vás.`,
    'guarantee.moneyBack.title': '100% späť znamená 100% späť',
    'guarantee.moneyBack.desc': `Žiadne „spracovateľské poplatky". Žiadne „administratívne náklady". Celá suma, do 5 dní.`,
    'guarantee.delivery.title': 'Meškáme? Platíte menej',
    'guarantee.delivery.desc': 'Za každý deň omeškania dostanete 5% zľavu. Pri 3 dňoch oneskorenia = 15% späť.',
    'guarantee.cta.title': 'Stále máte otázky?',
    'guarantee.cta.description': 'Zavolajte nám alebo napíšte. Odpovieme do 24 hodín.',
    'guarantee.cta.noRisk': 'Bez záväzkov',
    'guarantee.cta.guaranteed': 'Konzultácia zadarmo',
    
    // Testimonials
    'testimonials.badge': 'Recenzie klientov',
    'testimonials.title': 'Čo hovoria naši klienti',
    'testimonials.subtitle': 'Skutočné ohlasy od firiem, ktorým sme pomohli transformovať ich online prítomnosť',
    'testimonials.client1.name': 'Martin Novák',
    'testimonials.client1.role': 'CEO, TechStart',
    'testimonials.client1.text': 'Úžasná rýchlosť a kvalita. Naša webstránka bola hotová za 2 dni a vyzerá neuveriteľne profesionálne. AI prístup nám ušetril týždne vývoja.',
    'testimonials.client2.name': 'Jana Kováčová',
    'testimonials.client2.role': 'Marketing Manager, GrowthCo',
    'testimonials.client2.text': 'Bola som skeptická ohľadom AI generovaných stránok, ale výsledok prekonal všetky očakávania. Moderný dizajn, perfektná funkčnosť a neprekonateľná cena.',
    'testimonials.client3.name': 'Peter Horváth',
    'testimonials.client3.role': 'Zakladateľ, FitLife Studio',
    'testimonials.client3.text': 'Jediná chyba bola tá, že sme na našej strane nemali úplne jasnú víziu. Ale spoločne sme ju vedeli pretaviť do výsledku. Rýchla komunikácia, viaceré revízie v cene a finálny výsledok je ohromujúci. Vrelo odporúčam!',
    'testimonials.client4.name': 'Lucia Tóthová',
    'testimonials.client4.role': 'Majiteľka, Beauty Salon',
    'testimonials.client4.text': 'Naša nová webstránka nám prináša viac klientov ako kedykoľvek predtým. Profesionálny vzhľad, ľahká navigácia a funguje perfektne na mobiloch.',
    'testimonials.client5.name': 'Marek Szabó',
    'testimonials.client5.role': 'Riaditeľ, Právne služby',
    'testimonials.client5.text': 'Vynikajúca skúsenosť od začiatku do konca. Webstránka odráža náš profesionálny imidž a dostali sme ju ešte rýchlejšie ako bolo sľúbené.',
    'testimonials.client6.name': 'Rastislav Štulrajter',
    'testimonials.client6.role': 'CEO, FVEnergy',
    'testimonials.client6.text': 'Pomer kvality a ceny je neprekonateľný. Získali sme modernú, rýchlu stránku, ktorá zvýšila naše konverzie o 40% už v prvom mesiaci.',
    'testimonials.stats.projects': 'Dokončených projektov',
    'testimonials.stats.satisfaction': 'Miera spokojnosti',
    'testimonials.stats.rating': 'Priemerné hodnotenie',
    'testimonials.stats.response': 'Čas odpovede',
    
    // Pricing
    'pricing.title': 'Cenník',
    'pricing.subtitle': 'Vyberte si ideálny plán pre váš projekt',
    'pricing.description': 'Všetky plány obsahujú AI-powered dizajn, vývoj, optimalizáciu a spustenie.',
    'pricing.additionalNote': 'Po prekročení počtu revízií si môžete zakúpiť dodatočné hodiny, počas ktorých vykonáme úpravy. Každá začatá hodina stojí €60.',
    'pricing.customNote': 'Obsah a štruktúru webu prispôsobíme vašim potrebám',
    'pricing.getStarted': 'Začať',
    'pricing.oneTime': 'Jednorázovo',
    'pricing.starter.name': 'Landing Pro',
    'pricing.starter.useCase': 'Jedna silná stránka, ktorá premení návštevníkov na zákazníkov',
    'pricing.business.name': 'Web Complete',
    'pricing.business.useCase': 'Všetko čo vaša firma potrebuje v jednom kompletnom balíku',
    'pricing.premium.name': 'Brand Builder',
    'pricing.premium.useCase': 'Vybudujte nezabudnuteľnú značku s kompletnou digitálnou identitou',
    
    // Pricing Features
    'pricing.features.completeLanding': '1-3 stránkový web s hero sekciou, výhodami, referenciami a kontaktom',
    'pricing.features.multiPageWeb': 'Viacstránkový web s kompletnou štruktúrou',
    'pricing.features.extensiveWeb': 'Rozsiahly web s plným portfóliom a prezentáciou',
    'pricing.features.advancedSalesStructure': 'Prepracovaná predajná štruktúra',
    'pricing.features.contactIntegration': 'Integrácia kontaktného formulára',
    'pricing.features.mobileResponsive': 'Mobilný responzívny dizajn',
    'pricing.features.basicSEO': 'Základná SEO optimalizácia',
    'pricing.features.advancedSEO': 'Pokročilá SEO optimalizácia',
    'pricing.features.2revisions': '2 revízie zahrnuté v cene',
    'pricing.features.3dayDelivery': 'Express dodanie do 3 dní',
    'pricing.features.5dayDelivery': 'Dodanie do 5 pracovných dní',
    'pricing.features.advancedContactForms': 'Pokročilé kontaktné formuláre',
    'pricing.features.imageGallery': 'Galéria obrázkov/portfólio',
    'pricing.features.aiBanners': 'Tvorba AI bannerov/vizuálov',
    'pricing.features.faq': 'FAQ',
    'pricing.features.3revisions': '3 revízie zahrnuté v cene',
    'pricing.features.5revisions': '5 revízií zahrnutých v cene',
    'pricing.features.logoAI': 'Logo s AI',
    'pricing.features.specialColors': 'Prispôsobenie firemných farieb',
    'pricing.features.socialIntegration': 'Integrácia sociálnych médií',
    'pricing.features.7dayDelivery': 'Kompletné dodanie do 7 dní',
    'pricing.features.3to5Subpages': '3 - 5 podstránok',
    'pricing.features.5to8Subpages': '5+ podstránok',
    // Tiered animations
    'pricing.features.basicAnimations': 'Základné scroll animácie',
    'pricing.features.advancedAnimations': 'Pokročilé animácie a prechody',
    'pricing.features.premiumAnimations': 'Premium animácie + mikrointerakcie',
    // Exclusive features
    'pricing.features.analytics': 'Integrácia Google Analytics',
    'pricing.features.blogSection': 'Blog sekcia',
    'pricing.features.brandingGuide': 'Branding guide a štýlový manuál',
    'pricing.features.videoSection': 'Video sekcia / Video pozadie',
    'pricing.features.prioritySupport': 'Prioritné spracovanie',
    'pricing.features.apiIntegration': 'Napojenie na rôzne API (Calendly, kalendár, atď.)',
    
    'pricing.allInclude': 'Všetky balíky obsahujú',

    // Pricing Comparison Table
    'pricing.comparison.title': 'Porovnanie balíkov',
    'pricing.comparison.feature': 'Funkcia',
    'pricing.comparison.pages': 'Počet stránok',
    'pricing.comparison.aiBanners': 'AI bannery a vizuály',
    'pricing.comparison.contactForm': 'Kontaktný formulár',
    'pricing.comparison.gallery': 'Galéria / Portfólio',
    'pricing.comparison.faq': 'FAQ sekcia',
    'pricing.comparison.social': 'Integrácia sociálnych médií',
    'pricing.comparison.aiLogo': 'AI generované logo',
    'pricing.comparison.colorScheme': 'Prispôsobenie firemných farieb',
    'pricing.comparison.animations': 'Animácie',
    'pricing.comparison.responsive': 'Mobile responsive',
    'pricing.comparison.seo': 'SEO optimalizácia',
    'pricing.comparison.revisions': 'Počet revízií',
    'pricing.comparison.delivery': 'Čas dodania',
    'pricing.comparison.basic': 'Základný',
    'pricing.comparison.advanced': 'Pokročilý',
    'pricing.comparison.days': 'dní',
    'pricing.comparison.analytics': 'Integrácia Analytics',
    'pricing.comparison.blog': 'Blog sekcia',
    'pricing.comparison.brandingGuide': 'Branding guide',
    'pricing.comparison.videoSection': 'Video sekcia',
    'pricing.comparison.prioritySupport': 'Prioritné spracovanie',
    'pricing.comparison.apiIntegration': 'Napojenie na API',
    'pricing.comparison.animationsBasic': 'Základné',
    'pricing.comparison.animationsAdvanced': 'Pokročilé',
    'pricing.comparison.animationsPremium': 'Premium',
    'pricing.comparison.seoBasic': 'Základné',
    'pricing.comparison.seoAdvanced': 'Pokročilé',
    
    // Showcase
    'showcase.title': 'Takto môže vyzerať vaša webstránka',
    'showcase.subtitle': 'Inšpirujte sa modernými dizajnmi. Každá stránka je vytvorená na mieru vašim potrebám.',
    'showcase.description': 'Ukážky štýlov a funkcionalít, ktoré môžeme vytvoriť aj pre vás - od jednoduchých landing pages po komplexné riešenia.',
    'showcase.visitWebsite': 'Navštíviť webstránku',
    'showcase.example1.title': 'E-commerce obchod',
    'showcase.example1.desc': 'Moderný online obchod s katalógom produktov a nákupným košíkom',
    'showcase.example2.title': 'Webstránka reštaurácie',
    'showcase.example2.desc': 'Elegantná prezentácia reštaurácie s menu a rezerváciami',
    'showcase.example3.title': 'Tech Startup',
    'showcase.example3.desc': 'Inovatívna landing page startupu s moderným dizajnom',
    'showcase.ctaTitle': 'Páči sa vám, čo vidíte?',
    'showcase.ctaDescription': 'Do 3 dní môžete mať vlastnú webstránku v podobnom štýle.',
    'showcase.ctaButton': 'Chcem svoju stránku',
    
    // Website Types
    'websiteTypes.title': 'Pre koho tvoríme',
    'websiteTypes.subtitle': 'Nájdite sa medzi našimi klientmi a zistite, ako vám môžeme pomôcť',
    'websiteTypes.ecommerce.title': 'E-shopy & Produkty',
    'websiteTypes.ecommerce.desc': 'Predávate produkty online? Potrebujete webstránku, ktorá prezentuje váš tovar profesionálne a premieňa návštevníkov na zákazníkov.',
    'websiteTypes.ecommerce.identify': 'Mám produkt a chcem ho predávať',
    'websiteTypes.services.title': 'Služby & Freelanceri',
    'websiteTypes.services.desc': 'Ste kaderník, fotograf, účtovník alebo iný poskytovateľ služieb? Pomôžeme vám získať viac klientov a vybudovať dôveru online.',
    'websiteTypes.services.identify': 'Ponúkam služby a hľadám klientov',
    'websiteTypes.startups.title': 'Startupy & Projekty',
    'websiteTypes.startups.desc': 'Rozbieha sa váš nápad? Potrebujete rýchlo otestovať trh alebo získať investorov? Landing page, ktorá zaujme za pár dní.',
    'websiteTypes.startups.identify': 'Štartujem niečo nové',
    'websiteTypes.established.title': 'Firmy & Značky',
    'websiteTypes.established.desc': 'Máte fungujúci biznis, ale vaša webstránka zaostáva? Pomôžeme vám s modernou prezentáciou, ktorá reflektuje vašu kvalitu.',
    'websiteTypes.established.identify': 'Potrebujem zmenu / upgrade',
    
    // FAQ
    'faq.title': 'Často kladené otázky',
    'faq.subtitle': 'Všetko čo potrebujete vedieť o našej tvorbe webstránok s AI',
    'faq.q1': 'Ako rýchlo bude moja webstránka pripravená?',
    'faq.a1': 'V závislosti od balíčka, medzi 1 až 3 dňami. Náš AI-powered proces výrazne skracuje čas vývoja pri zachovaní vysokej kvality.',
    'faq.q2': 'Môžem webstránku neskôr aktualizovať?',
    'faq.a2': 'Áno. Môžeme rýchlo regenerovať nové verzie na základe nových promptov alebo úprav. Náš AI systém robí aktualizácie oveľa rýchlejšie ako tradičný vývoj.',
    'faq.q3': 'Čo je finálny výstup?',
    'faq.a3': 'Môžeme vám vygenerovať kompletný kód z tohto rozhrania a použiť ho kdekoľvek chcete. Alebo ho môžeme hostovať my, čo vám umožní kedykoľvek lahko robiť dodatočné úpravy.',
    'faq.q4': 'Je stránka SEO optimalizovaná?',
    'faq.a4': 'Áno. Vaša stránka obsahuje čisté hlavičky, metadata, rýchly výkon a optimalizáciu obrázkov. Dodržiavame aktuálne SEO best practices, aby vaša stránka dobre ranovala.',
    'faq.q5': 'Poskytujete hosting?',
    'faq.a5': 'Áno, poskytujeme spoľahlivý hosting s jednoduchým prístupom pre budúce úpravy. Alebo si môžete stiahnuť kód a hostovať ho kdekoľvek chcete - máte plnú flexibilitu.',
    'faq.q6': 'Čo ak nebudem spokojný s výsledkom?',
    'faq.a6': 'Úzko s vami spolupracujeme počas procesu dolaďovania, aby finálny výsledok splnil vaše očakávania. Náš iteratívny prístup znamená, že môžeme upravovať a zlepšovať, kým nebudete spokojní.',
    'faq.q7': 'Aký je rozdiel medzi balíkmi a ktorý si vybrať?',
    'faq.a7': 'Landing Pro (€799) je ideálny pre jednu landing page - uvedenie produktu, služby alebo kampane. Web Complete (€1,199) je naša najpopulárnejšia voľba pre firmy, ktoré potrebujú viacstránkový web s integráciou analytics a blog sekciou. Brand Builder (€1,899) je pre etablované značky vyžadujúce rozsiahlu webovú prezentáciu s branding guide, video sekciou a prioritným spracovaním. Ak si nie ste istí, Web Complete je najlepší pomer cena/hodnota pre väčšinu firiem.',
    'faq.q8': 'Aké sú možnosti platby a vystavujete faktúry?',
    'faq.a8': 'Akceptujeme bankové prevody a platby kartou. Platba prebieha po odsúhlasení návrhu - 50% vopred, 50% pri dokončení. Ku každej objednávke vystavujeme riadnu faktúru, ktorú môžete použiť na daňové odpočty ako firemný náklad.',
    'faq.q9': 'Čo sa stane po dokončení projektu? Aké sú možnosti podpory?',
    'faq.a9': 'Po dokončení získate kompletnú webstránku so všetkými súbormi. Ponúkame hosting s jednoduchým prístupom pre budúce úpravy. Ak budete neskôr potrebovať zmeny, môžete si objednať dodatočné hodiny za €60/hodina. Taktiež poskytujeme balíky priebežnej údržby pre klientov, ktorí potrebujú pravidelné aktualizácie a podporu.',
    'faq.q10': 'Ako funguje 14-dňová záruka?',
    'faq.a10': 'Po dodaní webstránky máte 14 dní na jej dôkladné otestovanie. Ak z akéhokoľvek dôvodu webstránka nesplní vaše očakávania a nenájdeme spoločné riešenie, vrátime vám celú platbu. Žiadne komplikované formuláre, žiadne výhovorky - jednoduché vrátenie peňazí do 5 pracovných dní.',
    'faq.q11': 'Čo ak potrebujem viac ako 3 kolá revízií?',
    'faq.a11': 'Každý balíček zahŕňa presne 3 kolá revízií - čo je zvyčajne viac ako dosť. Ak budete potrebovať ďalšie úpravy po využití všetkých 3 kôl, môžete si objednať extra revízie za €60/hodina. Vždy sa snažíme byť maximálne flexibilní, aby sme zaistili vašu úplnú spokojnosť.',
    'faq.q12': 'Čo sa stane, ak nestihnete dodanie včas?',
    'faq.a12': 'Stojíme si za našimi termínmi. Ak meškáme, dostanete 5% zľavu za každý deň omeškania. Napríklad: 3 dni meškanie = 15% späť z vašej platby. Je to náš záväzok, že buď dostanete webstránku včas, alebo zaplatíte menej. Jednoduché a férové.',
    
    // Cookies
    'cookies.title': 'Používame cookies',
    'cookies.description': 'Používame cookies na zlepšenie vášho zážitku z prehliadania, poskytovanie personalizovaného obsahu a analýzu návštevnosti. Kliknutím na "Prijať všetky" súhlasíte s použitím cookies.',
    'cookies.accept': 'Prijať všetky',
    'cookies.decline': 'Odmietnuť',
    'cookies.preferences': 'Nastavenia',
    
    // Cookie Preferences
    'cookiePreferences.title': 'Nastavenia cookies',
    'cookiePreferences.description': 'Používame cookies na zlepšenie vášho zážitku z prehliadania a analýzu návštevnosti. Nižšie si môžete prispôsobiť svoje preferencie cookies.',
    'cookiePreferences.necessary.title': 'Nevyhnutné cookies',
    'cookiePreferences.necessary.description': 'Tieto cookies sú nevyhnutné pre správne fungovanie webovej stránky. Nemožno ich vypnúť.',
    'cookiePreferences.analytics.title': 'Analytické cookies',
    'cookiePreferences.analytics.description': 'Tieto cookies nám pomáhajú porozumieť, ako návštevníci interagujú s našou stránkou zbieraním anonymných informácií.',
    'cookiePreferences.marketing.title': 'Marketingové cookies',
    'cookiePreferences.marketing.description': 'Tieto cookies sa používajú na sledovanie návštevníkov naprieč webovými stránkami pre zobrazenie relevantných reklám.',
    'cookiePreferences.savePreferences': 'Uložiť nastavenia',
    'cookiePreferences.acceptAll': 'Prijať všetky',
    
    // Contact
    'contact.title': 'Rezervujte si bezplatnú 15-minútovú konzultáciu',
    'contact.subtitle': 'Povieme si o vašom projekte, odpovieme na otázky a navrhneme najlepšie riešenie. Bez záväzkov - len 15 minút vášho času.',
    'contact.name': 'Meno',
    'contact.email': 'Email',
    'contact.phone': 'Telefón',
    'contact.currentWebsite': 'Aktuálna webstránka',
    'contact.websiteType': 'Aký typ webstránky potrebujete?',
    'contact.description': 'Opíšte svoj nápad alebo cieľ',
    'contact.namePlaceholder': 'Vaše celé meno',
    'contact.emailPlaceholder': 'váš.email@priklad.sk',
    'contact.phonePlaceholder': '+421 123 456 789',
    'contact.currentWebsitePlaceholder': 'https://www.vasastranka.sk',
    'contact.websiteTypePlaceholder': 'Vyberte typ webstránky',
    'contact.descriptionPlaceholder': 'Povedzte nám o svojom biznise, cieľovej skupine, cieľoch a špecifických požiadavkách...',
    'contact.submitRequest': 'Chcem bezplatnú konzultáciu',
    'contact.submitting': 'Odosiela sa...',
    'contact.freeConsultation': '15 minút konzultácie zadarmo',
    'contact.responseTime': 'Ozveme sa do 24 hodín',
    'contact.noCommitment': 'Bez záväzkov',
    'contact.bookConsultation': 'Chcem bezplatnú konzultáciu',
    'contact.requestSubmitted': 'Požiadavka odoslaná!',
    'contact.responseMessage': 'Ďakujeme! Do 24 hodín vás kontaktujeme a dohodneme si termín 15-minútovej konzultácie.',
    'contact.socialProof.since': 'Tvoríme weby od roku 2025',
    'contact.socialProof.clients': 'Dôverujú nám firmy po celom Slovensku',
    'contact.ecommerceWebsite': 'E-shop / Produktová stránka',
    'contact.servicesWebsite': 'Servisná / Firemná stránka',
    'contact.startupWebsite': 'Startup / Landing page',
    'contact.rebrandWebsite': 'Modernizácia existujúcej stránky',
    'contact.other': 'Iné',
    
    // Campaign Questions
    'contact.campaign.basicInfo': 'Základné info o kampani',
    'contact.campaign.goal': 'Aký je hlavný cieľ kampane?',
    'contact.campaign.goalPlaceholder': 'predaj, leady, registrácie, iné',
    'contact.campaign.product': 'Aký produkt/službu ideme promovať?',
    'contact.campaign.productPlaceholder': 'Opíšte váš produkt alebo službu',
    'contact.campaign.targetGroup': 'Aká je hlavná cieľová skupina?',
    'contact.campaign.targetGroupPlaceholder': 'vek, demografia, záujmy',
    
    'contact.campaign.contentOffer': 'Obsah & ponuka',
    'contact.campaign.mainOffer': 'Aká je hlavná ponuka alebo benefit pre návštevníka?',
    'contact.campaign.mainOfferPlaceholder': 'zľava, bezplatná skúška, konzultácia',
    'contact.campaign.contentReady': 'Máte už pripravený text/popis produktu, alebo ho máme vytvoriť?',
    'contact.campaign.contentReadyPlaceholder': 'pripravené / treba vytvoriť',
    'contact.campaign.media': 'Máte fotografie, video alebo grafiku, ktoré musíme použiť?',
    'contact.campaign.mediaPlaceholder': 'linky na materiály alebo "nie"',
    
    'contact.campaign.structure': 'Štruktúra & prvky stránky',
    'contact.campaign.formButton': 'Potrebujete na stránke formulár, tlačidlo na nákup alebo inú akciu?',
    'contact.campaign.formButtonPlaceholder': 'kontaktný formulár, tlačidlo nákupu, registrácia',
    'contact.campaign.sections': 'Aké sekcie sú nevyhnutné?',
    'contact.campaign.sectionsPlaceholder': 'úvod, benefity, recenzie, FAQ, kontakt',
    'contact.campaign.countdown': 'Má stránka obsahovať odpočítavanie času alebo limitovanú ponuku?',
    'contact.campaign.countdownPlaceholder': 'áno / nie',
    
    'contact.campaign.brand': 'Brand & štýl',
    'contact.campaign.colors': 'Aké sú farby a fonty vašej značky?',
    'contact.campaign.colorsPlaceholder': 'modrá, biela, Arial / alebo "vytvoriť nové"',
    'contact.campaign.tone': 'Má stránka pôsobiť formálne, priateľsky alebo prémiovo?',
    'contact.campaign.tonePlaceholder': 'formálne / priateľsky / prémiovo',
    'contact.campaign.examples': 'Máte príklady stránok, ktoré sa vám páčia?',
    'contact.campaign.examplesPlaceholder': 'linky alebo opisy',
    
    'contact.campaign.technical': 'Technické & praktické',
    'contact.campaign.url': 'Aká je plánovaná URL alebo doména?',
    'contact.campaign.urlPlaceholder': 'vášstránka.sk alebo subdoména',
    'contact.campaign.standalone': 'Má stránka bežať samostatne alebo byť súčasťou vášho webu?',
    'contact.campaign.standalonePlaceholder': 'samostatne / súčasť existujúceho webu',
    
    // Product Questions
    'contact.product.basicInfo': 'Základné info o produkte',
    'contact.product.type': 'Aký typ produktu predávate?',
    'contact.product.typePlaceholder': 'fyzický produkt, softvér, kurz, služba',
    'contact.product.uniqueness': 'Čím je váš produkt jedinečný?',
    'contact.product.uniquenessPlaceholder': 'kľúčové výhody a benefity',
    'contact.product.targetCustomer': 'Kto je váš ideálny zákazník?',
    'contact.product.targetCustomerPlaceholder': 'demografia, potreby, problémy',
    
    'contact.product.productDetails': 'Detaily produktu',
    'contact.product.features': 'Aké sú kľúčové vlastnosti?',
    'contact.product.featuresPlaceholder': 'vypíšte hlavné vlastnosti a benefity',
    'contact.product.pricing': 'Aký je váš cenový model?',
    'contact.product.pricingPlaceholder': 'jednorazovo, predplatné, stupňovité ceny',
    'contact.product.competition': 'Kto sú vaši hlavní konkurenti?',
    'contact.product.competitionPlaceholder': 'konkurenčné prostredie',
    
    'contact.product.presentation': 'Prezentácia produktu',
    'contact.product.showcase': 'Ako by sme mali prezentovať produkt?',
    'contact.product.showcasePlaceholder': 'demá, screenshoty, videá, referencie',
    'contact.product.callToAction': 'Aká je hlavná výzva k akcii?',
    'contact.product.callToActionPlaceholder': 'kúpiť teraz, vyskúšať zadarmo, požiadať o demo',
    'contact.product.trust': 'Aké prvky dôvery máte?',
    'contact.product.trustPlaceholder': 'referencie, recenzie, záruky, certifikáty',
    
    // Service Questions
    'contact.service.basicInfo': 'Základné info o službe',
    'contact.service.type': 'Aký typ služby poskytujete?',
    'contact.service.typePlaceholder': 'poradenstvo, opravy, dizajn, koučing',
    'contact.service.process': 'Aký je váš pracovný proces?',
    'contact.service.processPlaceholder': 'krok za krokom ako pracujete s klientmi',
    'contact.service.results': 'Aké výsledky klienti dosahujú?',
    'contact.service.resultsPlaceholder': 'príbehy úspechu, výsledky, benefity',
    
    'contact.service.positioning': 'Pozicionovanie služby',
    'contact.service.experience': 'Aké sú vaše skúsenosti/odbornosť?',
    'contact.service.experiencePlaceholder': 'roky, certifikáty, významné projekty',
    'contact.service.areas': 'Aké oblasti/odvetvia obsluhujete?',
    'contact.service.areasPlaceholder': 'geografické oblasti, odvetvia, špecializácie',
    'contact.service.approach': 'Aký je váš jedinečný prístup?',
    'contact.service.approachPlaceholder': 'metodológia, filozofia, odlíšenie',
    
    'contact.service.booking': 'Rezervácia & kontakt',
    'contact.service.consultation': 'Ponúkate bezplatnú konzultáciu?',
    'contact.service.consultationPlaceholder': 'áno / nie, trvanie, čo je zahrnuté',
    'contact.service.pricing': 'Ako sa cení vaša služba?',
    'contact.service.pricingPlaceholder': 'hodinová sadzba, za projekt, balíčky',
    'contact.service.bookingMethod': 'Ako si môžu klienti rezervovať/kontaktovať vás?',
    'contact.service.bookingMethodPlaceholder': 'online rezervácia, telefón, email, kontaktný formulár',
    
    // Landing Page Questions
    'contact.landing.basicInfo': 'Základné info o landing stránke',
    'contact.landing.source': 'Odkiaľ bude pochádzať návštevnosť?',
    'contact.landing.sourcePlaceholder': 'Google Ads, Facebook, email, organické',
    'contact.landing.goal': 'Aký je jediný hlavný cieľ?',
    'contact.landing.goalPlaceholder': 'prihlásenie, nákup, stiahnutie, registrácia',
    'contact.landing.offer': 'Aká je vaša neodolateľná ponuka?',
    'contact.landing.offerPlaceholder': 'zľava, bezplatná skúška, bonus, záruka',
    
    'contact.landing.optimization': 'Optimalizácia & konverzie',
    'contact.landing.headline': 'Aký by mal byť hlavný nadpis?',
    'contact.landing.headlinePlaceholder': 'presvedčivý nadpis, ktorý pritiahne pozornosť',
    'contact.landing.objections': 'Aké námietky môžu mať návštevníci?',
    'contact.landing.objectionsPlaceholder': 'cena, dôvera, čas, potreba',
    'contact.landing.urgency': 'Čo vytvára naliehavosť?',
    'contact.landing.urgencyPlaceholder': 'limitovaný čas, množstvo, bonus vyprší',
    
    'contact.landing.testing': 'Testovanie & analytika',
    'contact.landing.variants': 'Chcete testovať viacero verzií?',
    'contact.landing.variantsPlaceholder': 'A/B test nadpisov, ponúk, rozložení',
    'contact.landing.tracking': 'Aké sledovanie potrebujete?',
    'contact.landing.trackingPlaceholder': 'Google Analytics, Facebook Pixel, sledovanie konverzií',
    'contact.landing.followUp': 'Čo sa stane po konverzii?',
    'contact.landing.followUpPlaceholder': 'ďakovná stránka, email sekvencia, presmerovanie',
    
    // Rebrand Questions
    'contact.rebrand.currentState': 'Aktuálny stav webstránky',
    'contact.rebrand.problems': 'Aké problémy má vaša súčasná webstránka?',
    'contact.rebrand.problemsPlaceholder': 'zastaraný dizajn, pomalé načítanie, nie je mobilná, zlé UX',
    'contact.rebrand.keepFunctionality': 'Akú funkcionalitu chcete zachovať?',
    'contact.rebrand.keepFunctionalityPlaceholder': 'kontaktný formulár, blog, galéria, špecifické integrácie',
    'contact.rebrand.newRequirements': 'Nové požiadavky',
    'contact.rebrand.newFeatures': 'Aké nové funkcie alebo sekcie potrebujete?',
    'contact.rebrand.newFeaturesPlaceholder': 'online rezervácia, e-shop, referencie, portfólio',
    'contact.rebrand.goal': 'Aký je hlavný cieľ modernizácie?',
    'contact.rebrand.goalPlaceholder': 'viac klientov, lepšie konverzie, moderný vzhľad, zlepšené UX',
    'contact.rebrand.competitors': 'Ktoré webstránky konkurencie sa vám páčia?',
    'contact.rebrand.competitorsPlaceholder': 'konkurent1.sk, konkurent2.sk',
    'contact.rebrand.brandContent': 'Brand & obsah',
    'contact.rebrand.brandChanges': 'Bude sa meniť aj brand (logo, farby)?',
    'contact.rebrand.brandChangesPlaceholder': 'áno / nie / čiastočne',
    'contact.rebrand.contentUpdate': 'Máte aktualizované texty a fotografie?',
    'contact.rebrand.contentUpdatePlaceholder': 'áno / treba vytvoriť / čiastočne pripravené',
    'contact.rebrand.timeline': 'Kedy potrebujete novú webstránku?',
    'contact.rebrand.timelinePlaceholder': 'čo najskôr / do mesiaca / neponáhľa sa',
    
    // Footer
    'footer.description': 'Tvorba webstránok s AI. Od nápadu k prepracovanej webstránke za pár dní.',
    'footer.quickLinks': 'Rýchle odkazy',
    'footer.contact': 'Kontakt'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguage] = useState<Language>('sk');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};